import { useState, useEffect } from 'react';
import { ImageCacheService } from '../../../services/imageCacheService';

/**
 * Resolves a single image reference (local:/chat: cache id, http(s) URL, or blob:/data: URI) into a
 * displayable URL, or null if it can't be resolved (e.g. a local:/chat: blob that's since been
 * evicted from IndexedDB). Shared by useLocalUrl (single reactive value) and any caller that needs
 * to resolve a whole batch up front, e.g. ImageCarouselModal filtering out dead slides.
 */
export const resolveImageUrl = async (url: string | undefined | null): Promise<string | null> => {
  if (!url) return null;
  // Expanded previews receive the already-resolved local object URL.
  if (url.startsWith('blob:')) return url;

  if (url.startsWith('data:') || url.startsWith('http')) {
    // RESILIENCE: Fix malformed Supabase URLs that are missing the .supabase.co suffix
    // Common in staging: https://elupwlkmdytzekcubbkz/ -> https://elupwlkmdytzekcubbkz.supabase.co/
    let sanitized = url;
    const stagingId = (import.meta.env.VITE_PREVIEW_SUPABASE_URL || '').match(/\/\/([^.]+)\./)?.[1];
    const prodId = (import.meta.env.VITE_SUPABASE_URL || '').match(/\/\/([^.]+)\./)?.[1];
    if (stagingId && url.includes(stagingId) && !url.includes('supabase.co')) {
      sanitized = url.replace(stagingId, `${stagingId}.supabase.co`);
    } else if (prodId && url.includes(prodId) && !url.includes('supabase.co')) {
      sanitized = url.replace(prodId, `${prodId}.supabase.co`);
    }
    return sanitized;
  }

  if (url.startsWith('local:') || url.startsWith('chat:')) {
    // OPTIMIZATION: Prefer existing memory cache (force=false)
    // Only force a fresh resolution if the first one returns nothing
    const blobUrl = await ImageCacheService.resolveLocalUrl(url, false);
    if (blobUrl) return blobUrl;
    // If it failed (maybe URL revoked), try one final hard force from DB
    return await ImageCacheService.resolveLocalUrl(url, true);
  }

  return null;
};

// Confirms an http(s) URL actually loads rather than optimistically trusting the string -
// resolveImageUrl doesn't do this (SmartImage's own onError/placeholder handles it fine for
// normal display), but callers deciding whether to show a slide/button at all (ImageCarouselModal,
// the logbook's "Original Receipt" button) need to know in advance, since a Storage object can be
// gone even though the DB still holds a well-formed URL for it (e.g. deleted out from under a
// still-referenced row).
const imageLoads = (url: string): Promise<boolean> => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve(true);
  img.onerror = () => resolve(false);
  img.src = url;
});

/**
 * Like resolveImageUrl, but additionally verifies http(s) results actually load before returning
 * them (blob:/data: results from resolveImageUrl are already confirmed to exist by that point, so
 * only the http(s) branch needs the extra check). Use this when deciding whether to show UI that
 * depends on the image existing, not for normal display (SmartImage's own error handling covers
 * that more cheaply).
 */
export const resolveAndVerifyImageUrl = async (url: string | undefined | null): Promise<string | null> => {
  const resolved = await resolveImageUrl(url);
  if (!resolved) return null;
  if (resolved.startsWith('http')) {
    return (await imageLoads(resolved)) ? resolved : null;
  }
  return resolved;
};

/**
 * useLocalUrl Hook: Resolves local storage references (local:...) into
 * temporary browser URLs (blob:...).
 * PIVOT: Forces a fresh resolution from IndexedDB every time to ensure
 * visibility after navigation.
 */
export const useLocalUrl = (url: string | undefined | null) => {
  const [resolvedUrl, setResolvedUrl] = useState<string | null | undefined>(() => {
    if (!url) return null;
    if (!url.startsWith('local:') && !url.startsWith('chat:')) return url;

    // ATOMIC OPTIMIZATION: Check memory cache synchronously during init
    // This prevents the "flash of empty image" during list/map toggles
    return ImageCacheService.getCachedUrl(url) || undefined;
  });

  useEffect(() => {
    if (!url) {
      setResolvedUrl(null);
      return;
    }

    let isMounted = true;
    resolveImageUrl(url).then((resolved) => {
      if (isMounted) setResolvedUrl(resolved);
    });
    return () => { isMounted = false; };
  }, [url]);

  return resolvedUrl;
};
